"use client";

import { Suspense, useEffect, useRef } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import posthog from "posthog-js";
import { PostHogProvider as PHProvider, usePostHog } from "posthog-js/react";

import { User } from "@shared/proto";

import { env } from "~/env";

export function PostHogProvider({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    if (typeof window === "undefined" || !env.NEXT_PUBLIC_POSTHOG_KEY) return;
    posthog.init(env.NEXT_PUBLIC_POSTHOG_KEY, {
      api_host: env.NEXT_PUBLIC_POSTHOG_HOST,
      capture_pageview: false,
      loaded: (posthog) => {
        if (env.NODE_ENV === "development") posthog.opt_out_capturing();
      },
    });
  }, []);

  return <PHProvider client={posthog}>{children}</PHProvider>;
}

function PostHogPageview() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const posthog = usePostHog();

  useEffect(() => {
    if (!env.NEXT_PUBLIC_POSTHOG_KEY) return;
    if (!posthog) return;
    if (!pathname) return;

    let url = window.origin + pathname;
    if (searchParams && searchParams.toString()) {
      url = url + `?${searchParams.toString()}`;
    }
    posthog.capture("$pageview", {
      $current_url: url,
    });
  }, [posthog, pathname, searchParams]);

  return <></>;
}

function PostHogIdentifyUserView({ user }: { user: User | null }) {
  const posthog = usePostHog();
  const isIdentifyUser = useRef(false);

  useEffect(() => {
    if (!env.NEXT_PUBLIC_POSTHOG_KEY) return;
    if (!posthog) return;
    if (!user) {
      isIdentifyUser.current = false;
      return;
    }
    if (isIdentifyUser.current) return;

    posthog.identify(user.userId, {
      email: user.email,
      first_name: user.firstName,
      last_name: user.lastName,
    });
    posthog.alias(user.email);

    isIdentifyUser.current = true;
  }, [posthog, user]);

  return <></>;
}

// Wrap this in Suspense to avoid the `useSearchParams` usage above
// from de-opting the whole app into client-side rendering
// See: https://nextjs.org/docs/messages/deopted-into-client-rendering
export function SuspendedPostHogPageView() {
  return (
    <Suspense fallback={null}>
      <PostHogPageview />
    </Suspense>
  );
}

export function SuspendedPostHogIdentifyUserView({ user }: { user: User | null }) {
  return (
    <Suspense fallback={null}>
      <PostHogIdentifyUserView user={user} />
    </Suspense>
  );
}
